import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import {
  CriarItemRapidoDTO,
  CriarItemRapidoFormDTO,
  ItemDTO,
} from '../../../models/item.model';
import { ApiService } from '../../../services/api.service';
import { ItemsService } from '../../../services/financeiro/item.service';
import { PermissaoService } from '../../../services/permissao/permissao.service';

/**
 * Diálogo "Criar novo Item" — cadastro rápido de mercadoria.
 *
 * EXTRAÍDO de `produto-selecionar.component` (compra), onde era um `<p-dialog>`
 * inline no template, sem `@Input`/`@Output` e amarrado ao formulário de compra.
 * Agora é compartilhado entre:
 *   • `/adicionar-compra` (SelecionarItemComponent) — passa `fornecedorId` e
 *     auto-seleciona o item criado no próprio formulário de compra;
 *   • `/contador/operar` → aba "Nova venda" — SEM fornecedor; o item criado é
 *     encadeado para o modal de seleção, onde recebe o preço de venda.
 *
 * O item nasce com `precoDeVenda: 0` de propósito (não há campo de preço aqui).
 * Quem consome o `(criado)` é responsável por conduzir o preço — na venda, o
 * gate `precoInvalido` do seletor impede que um item a zero entre no carrinho.
 *
 * ⚠️ NÃO tem `<p-toast>` próprio nem `providers: [MessageService]`: usa o
 * MessageService do contexto em que é instanciado (o local do
 * `produto-selecionar` na compra; o da aplicação no Operar Cliente, cujo
 * `<p-toast>` vive em `operar-cliente.component.html`). Declarar um provider
 * aqui criaria uma segunda fila de mensagens sem `<p-toast>` para exibi-la.
 *
 * ⚠️ NÃO carrega `GET /configuracaoEmpresa/{id}` (moeda). Este diálogo não tem
 * campo monetário; derivar moeda da sessão é o padrão que causou o preço 100x
 * maior em USD (commit f106908d).
 */
@Component({
  selector: 'app-item-criar-rapido',
  templateUrl: './item-criar-rapido.component.html',
  styleUrls: ['./item-criar-rapido.component.scss'],
})
export class ItemCriarRapidoComponent implements OnChanges {
  /** Visibilidade do diálogo (two-way: `[(visible)]`). */
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * Fornecedor principal do item. OPCIONAL de propósito.
   *
   * Na compra é o fornecedor da nota; na venda NÃO EXISTE fornecedor — e o
   * bloqueio original (`if (!pessoaId) return`) impedia até a abertura do
   * diálogo. Quando ausente, o campo simplesmente não vai no payload:
   * `ItemDTO.fornecedorPrincipal_id` é `Long?` no backend e o
   * `ItemFormMapper` resolve com `?.let { }`.
   *
   * A pré-condição "escolha o fornecedor antes" continua sendo do fluxo de
   * COMPRA e permanece em `SelecionarItemComponent.abrirDialogCriarItem()`.
   */
  @Input() fornecedorId?: number | null;

  /** Item recém-criado (resposta do `POST /item`). */
  @Output() criado = new EventEmitter<ItemDTO>();

  salvando = false;
  casasDecimaisEstoque = 0;

  // Permissões dos botões "+" dos combos. Vêm do usuário LOGADO (na Área do
  // Contador, o contador — não o cliente). Semanticamente discutível, mas só
  // mostra/esconde os botões; comportamento preservado da origem.
  permGrupo = false;
  permSubgrupo = false;
  permMarca = false;
  permUnidade = false;

  form: CriarItemRapidoFormDTO = this.criarFormVazio();

  constructor(
    private apiService: ApiService,
    private itemService: ItemsService,
    private messageService: MessageService,
    private permissaoService: PermissaoService,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Prepara o formulário a cada ABERTURA (e não no ngOnInit): o componente
    // fica montado o tempo todo no pai, então só o flanco de subida do
    // `visible` significa "diálogo novo".
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.prepararFormulario();
    }
  }

  /** Fecha o diálogo sem salvar. */
  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  salvar(): void {
    const nome = this.form.nome?.trim();
    // O id da unidade é extraído ANTES de validar de propósito: o
    // `select-unidade-medida` pode ficar com um NÚMERO cru quando o id
    // pré-selecionado não existe na lista da empresa em contexto. Número é
    // truthy — validar `!form.unidadeMedida` deixaria passar e o payload sairia
    // com `unidadeMedidaDeItem_id: undefined`.
    const unidadeId = this.form.unidadeMedida?.unidadeMedidaDeItemEmpresa?.id;

    if (!nome || !this.form.grupo_id || !unidadeId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produto-selecionar.messages.atencao'),
        detail: this.translate.instant(
          'produto-selecionar.messages.preencha_campos_obrigatorios',
        ),
      });
      return;
    }

    this.salvando = true;

    const payload: CriarItemRapidoDTO = {
      nome,
      barras: this.form.barras?.trim() || null,
      grupo_id: this.form.grupo_id,
      subGrupo_id: this.form.subGrupo_id,
      marca_id: this.form.marca_id,
      unidadeMedidaDeItem_id: unidadeId,
      estoqueMinimo: this.form.estoqueMinimo || 0,
      // Sem fornecedor (venda) o campo vai `null` — o backend aceita.
      fornecedorPrincipal_id: this.fornecedorId ?? null,
      // Valores padrão fixos (o cadastro completo é feito em /produtos).
      precoDeVenda: 0,
      custo: 0,
      tipoDeImposto: 'IVA_ISENTO',
      tipoDeDesconto: 'SEM_DESCONTO',
      desconto: 0,
      status: true,
      saldo: 0,
      imagem: this.form.imagem
        ? this.form.imagem.split(',')[1] || this.form.imagem
        : null,
      tipoItem: 'MERCADORIA',
    };

    this.itemService.post('/item', payload as any).subscribe({
      next: (itemCriado: ItemDTO) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('produto-selecionar.messages.sucesso'),
          detail: this.translate.instant(
            'produto-selecionar.messages.item_criado_sucesso',
          ),
        });
        // Fecha ANTES de emitir: quem escuta costuma abrir outro diálogo por
        // cima (na venda, o seletor com o item pré-selecionado), e dois
        // `p-dialog` disputando o foco deixam o cursor no lugar errado.
        this.fechar();
        this.criado.emit(itemCriado);
      },
      error: (err) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('produto-selecionar.messages.erro'),
          detail:
            err?.error?.message ||
            this.translate.instant('produto-selecionar.messages.erro_criar_item'),
        });
      },
    });
  }

  onUnidadeMedidaChange(selecionada: any): void {
    this.casasDecimaisEstoque =
      selecionada?.unidadeMedidaDeItemEmpresa?.casasDecimais ?? 0;
    // Trocar a unidade muda a escala do estoque mínimo — zerar evita gravar
    // "2,50" de uma unidade em outra que só aceita inteiros.
    this.form.estoqueMinimo = null;
  }

  onImagemSelecionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const arquivo = input.files[0];
    if (arquivo.size > 2 * 1024 * 1024) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produto-selecionar.messages.atencao'),
        detail: this.translate.instant(
          'produto-selecionar.messages.imagem_muito_grande',
        ),
      });
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.form.imagem = reader.result as string;
    };
    reader.readAsDataURL(arquivo);
  }

  removerImagem(): void {
    this.form.imagem = null;
  }

  // ===========================================================================
  // Privados
  // ===========================================================================

  private prepararFormulario(): void {
    this.form = this.criarFormVazio();
    this.casasDecimaisEstoque = 0;
    this.salvando = false;

    this.permGrupo = this.permissaoService.temPermissao('grupo');
    this.permSubgrupo = this.permissaoService.temPermissao('subgrupo');
    this.permMarca = this.permissaoService.temPermissao('marca');
    this.permUnidade = this.permissaoService.temPermissao('unidade');

    this.preSelecionarUnidadePadrao();
  }

  private criarFormVazio(): CriarItemRapidoFormDTO {
    return {
      nome: '',
      barras: '',
      grupo_id: null,
      subGrupo_id: null,
      marca_id: null,
      unidadeMedida: undefined,
      estoqueMinimo: 0,
      imagem: null,
    };
  }

  /**
   * Conveniência: já deixa marcada a unidade de medida padrão da empresa.
   *
   * O `select-unidade-medida` com `returnType='object'` aceita receber o ID e
   * resolve o objeto internamente (`syncSelectedValue`). Quando o id não existe
   * na lista carregada — caso da Área do Contador, onde `/configuracaoGeral`
   * NÃO herda o contexto do cliente — o combo fica sem seleção e o usuário
   * escolhe na mão. É por isso que `salvar()` valida o id RESOLVIDO.
   */
  private preSelecionarUnidadePadrao(): void {
    this.apiService.read<any>('/configuracaoGeral').subscribe({
      next: (res: any) => {
        const idPadrao = res?.content?.[0]?.unidadeMedidaPadraoId;
        if (idPadrao) {
          this.form.unidadeMedida = idPadrao;
        }
      },
      error: () => {
        // Sem padrão o usuário escolhe a unidade na mão — nada quebra.
      },
    });
  }
}
